import { UnfoldMore } from '@mui/icons-material';
import {
    Button,
    CircularProgress,
    FormControl,
    Grid,
    InputAdornment,
    ListSubheader,
    MenuItem,
    Select as MuiSelect,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { MuiTextFieldWithFocus, MuiTextFieldWithFocus40 } from '../config/MaterialUIConstants';

// Style constants
const STYLES = {
    provinceSelect: {
        backgroundColor: '#fff',
        height: '50px',
        width: '68px',
        fontSize: '13px',
        letterSpacing: '0.15px',
        fontWeight: 400,
        color: '#212121',
        pl: 2,
        pr: 1,
        borderRight: 'none',
        border: 'none',
        borderRadius: '4px 0 0 4px',
        display: 'flex',
        alignItems: 'center',
        lineHeight: '150%',
        '& svg': {
            color: 'rgba(0, 0, 0, 0.57)',
            stroke: 1,
            marginRight: '10px',
            width: '18px',
            height: '18px',
            marginTop: '4px',
        },
        '& .MuiSelect-select': {
            backgroundColor: '#fff !important',
            paddingLeft: 0,
            borderRight: 'none',
            border: 'none',
        },
        '& .MuiSelect-select:focus': {
            backgroundColor: '#fff !important',
            borderRight: 'none',
            border: 'none',
        },
    },
    menuPaper: {
        maxHeight: '350px',
        mb: 1,
        borderRadius: '8px',
        border: '1px solid rgba(0, 0, 0, 0.10)',
        background: 'var(--White, #FFF)',
        boxShadow: '0 0 24px 0 rgba(0, 0, 0, 0.10)',
        overflow: 'hidden',
        padding: 0,
    },
    menuList: {
        maxHeight: '350px',
        overflowY: 'auto',
        padding: 0,
        pb: 1,
        '&::-webkit-scrollbar': {
            width: '6px',
        },
        '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '3px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '3px',
            '&:hover': {
                background: '#a8a8a8',
            },
        },
    },
    searchHeader: {
        backgroundColor: 'white',
        position: 'sticky',
        top: 0,
        zIndex: 2,
        padding: '8px 8px 4px 8px',
        borderBottom: '1px solid rgba(0, 0, 0, 0.10)',
        borderRadius: '8px 8px 0 0',
    },
    countryHeader: {
        p: 0,
        pt: 1.5,
        pl: '14px',
        fontWeight: 500,
        fontSize: '18px',
        color: '#006CDD',
        lineHeight: '150%',
        letterSpacing: '0.15px',
        textTransform: 'uppercase',
        backgroundColor: 'white',
        position: 'sticky',
        top: '61px',
        zIndex: 10,
    },
    provinceMenuItem: {
        pl: '14px',
        backgroundColor: 'white',
        '&:hover': {
            backgroundColor: '#f5f5f5',
        },
    },
    requiredAsterisk: { color: '#D32F2F' },
};

const LicensePlateInputV4 = ({
    control,
    errors,
    config,
    provinces,
    provinceValue,
    setProvinceValue,
    provinceLoader,
    isFleetCustomer = false,
    getValues,
    setValueInForm,
    clearErrors,
    shouldDecodeLicense, // Changed from setDecode and decode to shouldDecodeLicense ref
    handleDecodeLicense,
    decodeLicenseLoader = false,
}) => {
    const [searchText, setSearchText] = useState('');

    const provinceError = errors.provinceId?.message || '';
    const licenseError = errors.licenseNumber?.message || '';
    const hasProvinceError = !provinceValue && !!errors.provinceId;
    const hasError = !!errors.licenseNumber;

    // Group provinces by country and filter by search
    const groupedProvinces = useMemo(() => {
        if (!Array.isArray(provinces) || provinces.length === 0) return {};

        const shopCountryId = localStorage.getItem('shopCountryId') || '64e738882fec8f2c9b79c139';

        // Filter provinces by search text
        const filtered = provinces.filter((province) => {
            if (!province || !province.province || !province.provinceCode) {
                return false;
            }
            const provinceLowerCase = province.province.toLowerCase();
            const provinceCodeLowerCase = province.provinceCode.toLowerCase();
            const searchTextLowerCase = searchText.toLowerCase();
            return provinceLowerCase.includes(searchTextLowerCase) || provinceCodeLowerCase.includes(searchTextLowerCase);
        });

        // Group by country
        const grouped = filtered.reduce((acc, province) => {
            const countryName = province.country?.country || 'Unknown';
            if (!acc[countryName]) {
                acc[countryName] = [];
            }
            acc[countryName].push(province);
            return acc;
        }, {});

        // Find shop country name
        const shopCountry = provinces.find((province) => province.country?._id === shopCountryId)?.country?.country;

        // Create ordered object with shop country first, then alphabetical
        const orderedGrouped = {};

        // Add shop country first if it exists
        if (shopCountry && grouped[shopCountry]) {
            orderedGrouped[shopCountry] = grouped[shopCountry];
        }

        // Add remaining countries in alphabetical order
        Object.keys(grouped)
            .filter((country) => country !== shopCountry)
            .sort()
            .forEach((country) => {
                orderedGrouped[country] = grouped[country];
            });

        return orderedGrouped;
    }, [provinces, searchText]);

    const handleProvinceChange = (value) => {
        setProvinceValue(value);
        setValueInForm('provinceId', value);
        clearErrors('provinceId');
    };

    const handleLicenseChange = (e, field) => {
        const value = e.target.value?.toUpperCase();
        field.onChange(e);
        clearErrors('licenseNumber');
        setValueInForm('licenseNumber', value);
        shouldDecodeLicense.current = true; // Set ref flag to trigger decode
    };

    const scrollToSelectedProvince = () => {
        setTimeout(() => {
            const menuContainer = document.querySelector('.MuiPaper-root.MuiPopover-paper');
            if (!menuContainer) return;

            const selectedItem = menuContainer.querySelector(`[data-value="${provinceValue}"]`);
            if (selectedItem) {
                selectedItem.scrollIntoView({
                    block: 'center',
                    behavior: 'smooth',
                });
            }
        }, 100);
    };

    return (
        <Grid item xs={12} md={isFleetCustomer ? 6 : 12}>
            <Stack gap={1}>
                <Typography style={{ fontWeight: 500, marginBottom: '8px', color: '#212121' }}>
                    Licence Plate Number
                    {config?.isLicensePlateRequired && <span style={STYLES.requiredAsterisk}> *</span>}
                </Typography>
            </Stack>

            <Controller
                name="licenseNumber"
                control={control}
                rules={{
                    required: config?.isLicensePlateRequired ? 'Licence Plate Number is required' : false,
                }}
                render={({ field }) => (
                    <TextField
                        fullWidth
                        variant="outlined"
                        error={hasProvinceError || hasError}
                        helperText={hasProvinceError ? provinceError : hasError ? licenseError : ''}
                        {...field}
                        onChange={(e) => handleLicenseChange(e, field)}
                        id="outlined-license"
                        sx={{
                            ...MuiTextFieldWithFocus,
                            '& .MuiOutlinedInput-root': {
                                paddingLeft: 0,
                                input: {
                                    paddingLeft: '16px',
                                },
                            },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ marginRight: 0 }}>
                                    <Controller
                                        name="provinceId"
                                        control={control}
                                        rules={{
                                            required: config?.isLicensePlateRequired ? 'Province is required' : false,
                                        }}
                                        render={({ field: provinceField }) => (
                                            <FormControl>
                                                <MuiSelect
                                                    value={provinceValue || ''}
                                                    displayEmpty
                                                    onChange={(event) => handleProvinceChange(event.target.value)}
                                                    onClose={() => setSearchText('')}
                                                    onOpen={scrollToSelectedProvince}
                                                    variant="standard"
                                                    disableUnderline
                                                    IconComponent={UnfoldMore}
                                                    renderValue={(selected) => {
                                                        const selectedProvince = provinces?.find((m) => m._id === selected);
                                                        return selectedProvince?.provinceCode || '';
                                                    }}
                                                    sx={STYLES.provinceSelect}
                                                    MenuProps={{
                                                        autoFocus: false,
                                                        anchorOrigin: {
                                                            vertical: 'top',
                                                            horizontal: 'left',
                                                        },
                                                        transformOrigin: {
                                                            vertical: 'bottom',
                                                            horizontal: 'left',
                                                        },
                                                        PaperProps: {
                                                            sx: STYLES.menuPaper,
                                                        },
                                                        MenuListProps: {
                                                            sx: STYLES.menuList,
                                                        },
                                                    }}
                                                >
                                                    <ListSubheader sx={STYLES.searchHeader}>
                                                        <TextField
                                                            autoFocus
                                                            placeholder="Search Province or State"
                                                            fullWidth
                                                            value={searchText}
                                                            onChange={(e) => setSearchText(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key !== 'Escape') {
                                                                    e.stopPropagation();
                                                                }
                                                            }}
                                                            InputProps={{
                                                                startAdornment: (
                                                                    <svg
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        width="16"
                                                                        height="16"
                                                                        viewBox="0 0 16 16"
                                                                        fill="none"
                                                                        style={{ marginRight: '10px' }}
                                                                    >
                                                                        <path
                                                                            d="M12.0801 12.1L14.6468 14.6667M13.8359 7.58334C13.8359 9.24095 13.1775 10.8307 12.0054 12.0028C10.8333 13.1749 9.24354 13.8333 7.58594 13.8333C5.92833 13.8333 4.33862 13.1749 3.16652 12.0028C1.99442 10.8307 1.33594 9.24095 1.33594 7.58334C1.33594 5.92574 1.99442 4.33603 3.16652 3.16393C4.33862 1.99182 5.92833 1.33334 7.58594 1.33334C9.24354 1.33334 10.8333 1.99182 12.0054 3.16393C13.1775 4.33603 13.8359 5.92574 13.8359 7.58334Z"
                                                                            stroke="#6E6E6E"
                                                                            strokeWidth="1.5"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                ),
                                                            }}
                                                            sx={{
                                                                ...MuiTextFieldWithFocus40,
                                                                '& .MuiOutlinedInput-root': {
                                                                    backgroundColor: 'white',
                                                                },
                                                            }}
                                                        />
                                                    </ListSubheader>

                                                    {Object.entries(groupedProvinces).map(([country, countryProvinces]) => [
                                                        <ListSubheader key={`header-${country}`} sx={STYLES.countryHeader}>
                                                            {country === 'United States' ? 'USA' : country.toUpperCase()}
                                                        </ListSubheader>,
                                                        ...countryProvinces.map((province) => (
                                                            <MenuItem value={province._id} key={province._id} sx={STYLES.provinceMenuItem}>
                                                                {province.province}
                                                            </MenuItem>
                                                        )),
                                                    ])}

                                                    {Object.keys(groupedProvinces).length === 0 && !provinceLoader && (
                                                        <MenuItem disabled>No provinces found</MenuItem>
                                                    )}

                                                    {provinceLoader && (
                                                        <MenuItem disabled>
                                                            <CircularProgress size={16} />
                                                        </MenuItem>
                                                    )}
                                                </MuiSelect>
                                            </FormControl>
                                        )}
                                    />
                                </InputAdornment>
                            ),
                            endAdornment:
                                config?.isLicensePlateDecodeEnabled && getValues('licenseNumber') ? (
                                    <InputAdornment position="end">
                                        <Button onClick={handleDecodeLicense} disabled={decodeLicenseLoader}>
                                            {decodeLicenseLoader ? <CircularProgress size={16} /> : 'Decode'}
                                        </Button>
                                    </InputAdornment>
                                ) : null,
                        }}
                    />
                )}
            />
        </Grid>
    );
};

export default LicensePlateInputV4;
